import React, { useState, useEffect, useRef } from "react";
import { API } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const QUICK_PROMPTS = [
  { icon: "📋", label: "Resumen de cuentas", prompt: "Haceme un resumen bien sintético de lo que se está trabajando en cada cuenta." },
  { icon: "🚨", label: "Atención urgente", prompt: "¿Qué clientes tienen señales de riesgo o necesitan atención urgente? Basate en la actividad reciente." },
  { icon: "📅", label: "Actividad semanal", prompt: "¿Qué se estuvo trabajando esta semana en la agencia? Dame un resumen por cliente." },
  { icon: "⏳", label: "Entregas pendientes", prompt: "¿Qué entregas o tareas parecen estar pendientes o en curso según las conversaciones recientes?" },
  { icon: "💡", label: "Oportunidades upselling", prompt: "¿En qué cuentas hay oportunidades de upselling o crecimiento según las conversaciones recientes?" },
  { icon: "🔇", label: "Sin actividad", prompt: "¿Qué cuentas no tuvieron actividad en los últimos 30 días?" },
];

function exportContent(text, filename) {
  const clean = (text || "").replace(/\[EXPORTABLE\]/gi, "").trim();
  const blob = new Blob([clean], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `director-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function MessageBubble({ msg, onCopied }) {
  const isUser = msg.role === "user";
  const displayText = (msg.content || "").replace(/\[EXPORTABLE\]/gi, "").trim();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayText);
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = displayText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      alignItems: isUser ? "flex-end" : "flex-start",
      marginBottom: 20,
    }}>
      <div style={{
        maxWidth: "80%",
        background: isUser ? "var(--accent)" : "var(--bg-surface)",
        color: isUser ? "#fff" : "var(--text-primary)",
        padding: "12px 16px",
        borderRadius: isUser ? "18px 18px 4px 18px" : "4px 18px 18px 18px",
        fontSize: 14,
        lineHeight: 1.65,
        border: isUser ? "none" : "1px solid var(--border)",
        wordBreak: "break-word",
      }}>
        {isUser ? (
          <span style={{ whiteSpace: "pre-wrap" }}>{displayText}</span>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="md-content"
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer"
                   style={{ color: "var(--accent)", textDecoration: "underline" }}>
                  {children}
                </a>
              )
            }}
          >
            {displayText}
          </ReactMarkdown>
        )}
      </div>

      {/* Action buttons — only on assistant messages */}
      {!isUser && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button
            onClick={handleCopy}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "4px 10px",
              fontSize: 12,
              color: copied ? "var(--success)" : "var(--text-muted)",
              cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
              transition: "color 0.15s, border-color 0.15s",
              fontFamily: "inherit",
            }}
            onMouseEnter={e => { if (!copied) { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-strong)"; } }}
            onMouseLeave={e => { if (!copied) { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; } }}
          >
            {copied ? "✓ Copiado" : "⎘ Copiar"}
          </button>
          <button
            onClick={() => exportContent(msg.content)}
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: "4px 10px",
              fontSize: 12,
              color: "var(--text-muted)",
              cursor: "pointer",
              display: "flex", alignItems: "center", gap: 4,
              transition: "color 0.15s, border-color 0.15s",
              fontFamily: "inherit",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.borderColor = "var(--border-strong)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border)"; }}
          >
            ⬇ Exportar
          </button>
        </div>
      )}
    </div>
  );
}

export default function DirectorChat() {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [limitAlert, setLimitAlert] = useState(null);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // Load existing conversation
  useEffect(() => {
    API.get("/api/director/conversation")
      .then(r => {
        if (r.data) {
          setConversationId(r.data.id);
          setMessages(Array.isArray(r.data.messages) ? r.data.messages : []);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || sending) return;

    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setInput("");
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
    setSending(true);

    try {
      const r = await API.post("/api/director/chat", {
        messages: newMessages,
        conversationId,
      });
      setMessages(prev => [...prev, { role: "assistant", content: r.data.message }]);
      if (!conversationId) setConversationId(r.data.conversationId);
    } catch (err) {
      if (err.response?.status === 429 && err.response?.data?.error === "global_monthly_limit_exceeded") {
        setLimitAlert({
          current_usd: err.response.data.current_usd,
          limit_usd:   err.response.data.limit_usd,
        });
      } else {
        toast(err.response?.data?.error || "Error al enviar mensaje", "error");
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isEmpty = messages.length === 0 && !loadingHistory;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 0px)", maxHeight: "100%", overflow: "hidden" }}>

      {/* Header */}
      <div style={{
        padding: "14px 24px",
        borderBottom: "1px solid var(--border)",
        background: "var(--bg-surface)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexShrink: 0,
      }}>
        <div style={{
          width: 38, height: 38, borderRadius: "var(--radius-lg)",
          background: "linear-gradient(135deg, #0b00f9, #7c3aed)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, flexShrink: 0,
        }}>
          🎬
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 15, color: "var(--text-primary)" }}>Director</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Visión global de la agencia · últimos 30 días</div>
        </div>
      </div>

      {/* Messages area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>

        {loadingHistory && (
          <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
            <div className="spinner" />
          </div>
        )}

        {/* Empty state — minimal, cards are in the carousel below */}
        {isEmpty && (
          <div style={{ textAlign: "center", padding: "60px 24px 0" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🎬</div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>
              ¿En qué podés ayudarte?
            </h2>
            <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, maxWidth: 380, margin: "0 auto" }}>
              Tengo acceso a toda la actividad de la agencia en los últimos 30 días.
              Usá las sugerencias de abajo o escribí lo que necesites.
            </p>
          </div>
        )}

        {/* Message list */}
        {messages.length > 0 && (
          <div style={{ maxWidth: 800, margin: "0 auto" }}>
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={msg}
                onCopied={() => toast("Copiado al portapapeles", "success")}
              />
            ))}
            {sending && (
              <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 16 }}>
                <div style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "4px 18px 18px 18px",
                  padding: "12px 16px",
                  display: "flex", gap: 6, alignItems: "center",
                }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: "var(--text-muted)",
                      animation: `dotBounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Limit alert banner */}
      {limitAlert && (
        <div style={{
          padding: "14px 24px",
          borderTop: "1px solid rgba(239,68,68,0.3)",
          background: "rgba(239,68,68,0.08)",
          flexShrink: 0,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>🚫</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--danger)", marginBottom: 2 }}>
              Sin créditos disponibles este mes
            </div>
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              Se alcanzó el límite mensual de uso de la plataforma (<strong>USD ${parseFloat(limitAlert.limit_usd).toFixed(2)}</strong>).
              Por favor contactá con soporte para ampliar el límite y seguir usando los agentes.
            </div>
          </div>
        </div>
      )}

      {/* Quick prompts carousel — always visible above input */}
      {!limitAlert && <>
      <div style={{
        borderTop: "1px solid var(--border)",
        padding: "10px 24px 0",
        background: "var(--bg-surface)",
        flexShrink: 0,
      }}>
        <div style={{
          maxWidth: 800,
          margin: "0 auto",
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingBottom: 10,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}>
          {QUICK_PROMPTS.map((qp, i) => (
            <button
              key={i}
              onClick={() => sendMessage(qp.prompt)}
              disabled={sending}
              style={{
                background: "var(--bg-elevated)",
                border: "1px solid var(--border)",
                borderRadius: 99,
                padding: "5px 12px",
                fontSize: 12,
                color: "var(--text-secondary)",
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "flex", alignItems: "center", gap: 5,
                transition: "border-color 0.15s, background 0.15s, color 0.15s",
                fontFamily: "inherit",
                flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; e.currentTarget.style.background = "var(--bg-base)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.background = "var(--bg-elevated)"; }}
            >
              <span style={{ fontSize: 13 }}>{qp.icon}</span>
              {qp.label}
            </button>
          ))}
        </div>
      </div>

      {/* Input area */}
      <div style={{
        padding: "0 24px 16px",
        background: "var(--bg-surface)",
        flexShrink: 0,
      }}>
        <div style={{ maxWidth: 800, margin: "0 auto", display: "flex", gap: 10, alignItems: "flex-end" }}>
          <textarea
            ref={textareaRef}
            className="textarea"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Preguntá sobre cualquier cuenta, proyecto o entrega…"
            rows={1}
            style={{
              flex: 1,
              minHeight: 44,
              maxHeight: 160,
              resize: "none",
              lineHeight: 1.5,
              fontSize: 14,
              overflowY: "auto",
            }}
            onInput={e => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
            }}
            disabled={sending}
          />
          <button
            className="btn btn-primary"
            onClick={() => sendMessage()}
            disabled={sending || !input.trim()}
            style={{ height: 44, paddingLeft: 20, paddingRight: 20, flexShrink: 0 }}
          >
            {sending ? <span className="spinner" style={{ width: 14, height: 14 }} /> : "Enviar"}
          </button>
        </div>
        <div style={{ maxWidth: 800, margin: "4px auto 0", fontSize: 11, color: "var(--text-muted)" }}>
          Enter para enviar · Shift+Enter para nueva línea
        </div>
      </div>
      </>}

      <style>{`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        /* Hide scrollbar on carousel */
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
}
